
import React, { useState, useRef, useEffect } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import { Users, Bell } from 'lucide-react';
import NotificationCenter from './NotificationCenter';

const SelectedTeamsBar: React.FC = () => {
    const { userProfile, myOrgTeams, getTeamColor, toggleTeam, notifications } = useStandUp();
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const notifRef = useRef<HTMLDivElement>(null);
    
    const activeTeamIds = userProfile?.activeTeamIds || [];
    const unreadCount = notifications.filter(n => !n.isRead).length;

    // Close notifications when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
                setIsNotificationsOpen(false);
            }
        };
        if (isNotificationsOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isNotificationsOpen]);
    
    if (myOrgTeams.length === 0) return null;

    return (
        <div className="flex items-start justify-between w-full min-w-0">
            <div className="flex items-center gap-2 flex-wrap px-1 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-gray-400 mr-1 shrink-0">
                    <Users size={14} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">My Teams:</span>
                </div>
                {myOrgTeams.map(team => {
                    const teamId = team.id;
                    const isActive = activeTeamIds.includes(teamId);
                    const name = team.name;
                    const color = getTeamColor(teamId);
                    
                    return (
                        <button 
                            key={teamId}
                            onClick={() => toggleTeam(teamId)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 ${
                                isActive 
                                ? `${color.style} border-brand-200 dark:border-brand-800` 
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 border-gray-200 dark:border-gray-700 opacity-50 grayscale'
                            }`}
                        >
                            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? color.dot : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                            {name}
                        </button>
                    );
                })}
            </div>

            {/* NOTIFICATION BELL */}
            <div className="relative shrink-0 ml-4" ref={notifRef}>
                <button
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className={`p-2 rounded-xl transition-all relative ${isNotificationsOpen ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400' : 'text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20'}`}
                    title="Notifications"
                >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center animate-in zoom-in duration-300">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </div>
                    )}
                </button>
                
                <NotificationCenter isOpen={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} />
            </div>
        </div>
    );
};

export default SelectedTeamsBar;
